import { z } from 'zod';

/** Types communs du contrat (architecture §3.1 « common » et conventions d'API §3.2). */
export * from './fraction.js';

/** Identifiant UUID (v7 généré côté application, triable). */
export const IdSchema = z.uuid().meta({ id: 'Id', description: 'Identifiant UUID (v7, triable).' });
export type Id = z.infer<typeof IdSchema>;

export const MoneySchema = z
  .object({
    amountCents: z.number().int().refine(Number.isSafeInteger, 'montant hors des entiers sûrs'),
    currency: z.literal('EUR'),
  })
  .meta({ id: 'Money', description: 'Montant en centimes entiers et sa devise.' });
export type Money = z.infer<typeof MoneySchema>;

const dateExiste = (s: string): boolean => {
  const [a, m, j] = s.split('-').map(Number) as [number, number, number];
  const d = new Date(Date.UTC(a, m - 1, j));
  return d.getUTCFullYear() === a && d.getUTCMonth() === m - 1 && d.getUTCDate() === j;
};

export const LocalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'date AAAA-MM-JJ attendue')
  .refine(dateExiste, 'date impossible')
  .meta({ id: 'LocalDate', description: 'Date civile ISO 8601 (AAAA-MM-JJ), sans heure.', example: '2026-10-14' });
export type LocalDate = z.infer<typeof LocalDateSchema>;

export const InstantSchema = z.iso
  .datetime({ offset: true })
  .meta({ id: 'Instant', description: 'Instant ISO 8601 avec décalage explicite.', example: '2026-10-14T09:00:00-04:00' });
export type Instant = z.infer<typeof InstantSchema>;

export const DateRangeSchema = z
  .object({ from: LocalDateSchema, to: LocalDateSchema })
  .refine((p) => p.from <= p.to, { message: 'période inversée', path: ['to'] })
  .meta({ id: 'DateRange', description: 'Période civile inclusive.' });

export const PageQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/** Enveloppe de page par curseur. */
export const pageDe = <T extends z.ZodType>(element: T) => z.object({ items: z.array(element), nextCursor: z.string().nullable() });

export const CODES_ERREUR = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'MANDATE_REQUIRED',
  'IMPERSONATION_READ_ONLY',
  'CONSENT_MISSING',
  'CONTACT_RESTRICTED',
  'WEEKLY_CAP_REACHED',
  'OUTSIDE_SENDING_HOURS',
  'VERSION_CONFLICT',
  'PRECONDITION_REQUIRED',
  'IDEMPOTENCY_KEY_REUSED',
  'CONFIRMATION_REQUIRED',
  'OTP_REQUIRED',
  'LINK_EXPIRED',
  'SIGNATURE_INVALID',
  'INVOICE_IMMUTABLE',
  'RATE_LIMITED',
] as const;
export type CodeErreur = (typeof CODES_ERREUR)[number];

export const TYPE_PROBLEME = 'application/problem+json';

export const ProblemDetailsSchema = z
  .object({
    type: z.string().min(1),
    title: z.string().min(1),
    status: z.number().int().min(400).max(599),
    detail: z.string().optional(),
    instance: z.string().optional(),
    code: z.enum(CODES_ERREUR),
    errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  })
  .meta({ id: 'ProblemDetails', description: 'Erreur RFC 9457 avec un code stable.' });
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

export const EN_TETES = { idempotence: 'Idempotency-Key', siMatch: 'If-Match' } as const;

export const IdempotencyKeySchema = z.string().min(16).max(128).meta({ description: 'Clé d’idempotence (UUID recommandé).' });
export const IfMatchSchema = z
  .string()
  .regex(/^\d+$/)
  .meta({ description: 'row_version attendue de la ressource (concurrence optimiste).' });
